import {useCallback, useEffect, useRef, useState} from "react";
import {FiEye, FiClock, FiVideo, FiPercent} from "react-icons/fi";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import PageHeader from "../../../components/common/pageheader/PageHeader";
import {getMyExams} from "../../../services/examsApi";
import {getExamAttempts} from "../../../services/examAttemptsApi";
import {useAuth} from "../../../context/AuthContext";
import TableEmptyState from "../../../components/common/tableEmptyState/TableEmptyState";
import styles from "./MyExamsPage.module.css";
import Loader from "../../../components/common/Loader/Loader";
import {toStatusAz} from "../../../utils/statusText";
import {formatDateTime} from "../../../utils/formatDateTime";

// Re-check a beat after the scheduled start rather than exactly on it, so a sub-second
// disagreement between the two clocks does not cost a whole retry round.
const START_CHECK_LEAD_MS = 1000;
// Every student's wake-up is corrected to the SERVER clock, which is exactly what makes the whole
// cohort fire at the same instant — a synchronized burst of GET /exams/my that eats into the
// tenant-wide rate limit in one second. Each mount adds its own random slice of this window to
// every delay, spreading the burst out; a few seconds late is invisible next to the retry loop.
const WAKE_JITTER_MS = 4000;
// The exam is due but the server still says ACTIVE — the every-30s scheduler has not run yet,
// or the clocks disagree. Keep asking, backing off so a wedged scheduler cannot turn a page
// left open in a lab into a permanent 5-second poll.
const DUE_RETRY_BASE_MS = 5000;
const DUE_RETRY_MAX_MS = 60000;
// Never sleep longer than this in one go: it keeps the clock offset fresh over a long wait and
// picks up exams assigned after the page was opened.
const MAX_SLEEP_MS = 10 * 60 * 1000;

const examKey = (exam) => exam.examId ?? exam.id;

/** The exam can be entered right now. Must stay the single definition — the auto-refresh below
 *  decides when to stop polling from it, and the footer decides whether to draw the button. */
const isStartable = (exam) => {
    const status = (exam.status || "").toUpperCase();
    const myStatus = (exam.myStatus || "").toUpperCase();
    return status === "STARTED" && ["", "RE_ENTER", "IN_PROGRESS"].includes(myStatus);
};

/** Not startable yet, but only the clock stands in the way — worth watching for. */
const isPendingStart = (exam) => {
    const status = (exam.status || "").toUpperCase();
    const myStatus = (exam.myStatus || "").toUpperCase();
    return status === "ACTIVE" && ["", "RE_ENTER", "IN_PROGRESS"].includes(myStatus);
};

const MyExamsPage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {user} = useAuth() || {};
    const studentNumber = user?.studentNumber;

    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [resultLoading, setResultLoading] = useState(false);
    const [errorKey, setErrorKey] = useState("");
    // Bumped after every fetch, successful or not, so the scheduling effect below re-arms even
    // when the list came back identical or the request failed. Without it a single failed poll
    // would end the refresh loop and put the student back to refreshing by hand.
    const [refreshTick, setRefreshTick] = useState(0);

    const serverOffsetRef = useRef(0);
    const refreshTimerRef = useRef(null);
    const dueRetriesRef = useRef(0);
    // One draw per mount: this student's fixed offset within the fleet, applied to every wake-up
    // (first check and each backoff round alike, so the retries stay spread out too).
    const wakeJitterRef = useRef(Math.floor(Math.random() * WAKE_JITTER_MS));
    // null until the first load, so arriving at an already-open exam does not fire the toast.
    const startableIdsRef = useRef(null);

    const serverNow = useCallback(() => Date.now() + serverOffsetRef.current, []);

    // Deliberately dependency-free: this is what the refresh timer and the mount effect call, and
    // it must keep the same identity for the life of the page. Holding `t` here would rebuild it
    // whenever the translator's identity changed, re-running the mount effect and re-fetching in a
    // loop — hence the error is stored as a key and translated at render time instead.
    const fetchMyExams = useCallback(async ({silent = false} = {}) => {
        if (!silent) {
            setLoading(true);
        }
        try {
            const {exams: data, serverTime} = await getMyExams();
            if (serverTime) {
                serverOffsetRef.current = serverTime - Date.now();
            }
            setExams(Array.isArray(data) ? data : []);
            setErrorKey("");
        } catch {
            // A failed background poll leaves the list on screen untouched; only the load the
            // student actually waited for is worth an error banner.
            if (!silent) {
                setErrorKey("pages.myExams.loadError");
            }
        } finally {
            if (!silent) {
                setLoading(false);
            }
            setRefreshTick((tick) => tick + 1);
        }
    }, []);

    useEffect(() => {
        fetchMyExams();
    }, [fetchMyExams]);

    // Ask again exactly when something can change, instead of making the student refresh.
    useEffect(() => {
        window.clearTimeout(refreshTimerRef.current);

        const pending = exams.filter(isPendingStart);
        if (pending.length === 0) {
            dueRetriesRef.current = 0;
            return undefined;
        }

        const now = serverNow();
        let delay = MAX_SLEEP_MS;
        let anyDue = false;
        pending.forEach((exam) => {
            const startAt = exam.scheduledStartTime ? Date.parse(exam.scheduledStartTime) : NaN;
            if (Number.isNaN(startAt)) {
                return;
            }
            if (startAt <= now) {
                anyDue = true;
                return;
            }
            delay = Math.min(delay, (startAt - now) + START_CHECK_LEAD_MS);
        });

        if (anyDue) {
            delay = Math.min(delay, Math.min(DUE_RETRY_BASE_MS * (dueRetriesRef.current + 1), DUE_RETRY_MAX_MS));
            dueRetriesRef.current += 1;
        } else {
            dueRetriesRef.current = 0;
        }

        refreshTimerRef.current = window.setTimeout(
            () => fetchMyExams({silent: true}),
            Math.max(delay, 0) + wakeJitterRef.current
        );
        return () => window.clearTimeout(refreshTimerRef.current);
    }, [exams, refreshTick, fetchMyExams, serverNow]);

    // Browsers throttle timers in background tabs, so coming back to the tab re-checks directly;
    // the same applies after the laptop wakes up or the connection returns.
    useEffect(() => {
        const refreshIfVisible = () => {
            if (document.visibilityState === "visible") {
                fetchMyExams({silent: true});
            }
        };
        document.addEventListener("visibilitychange", refreshIfVisible);
        window.addEventListener("online", refreshIfVisible);
        return () => {
            document.removeEventListener("visibilitychange", refreshIfVisible);
            window.removeEventListener("online", refreshIfVisible);
        };
    }, [fetchMyExams]);

    // The button appearing is easy to miss if the student is looking elsewhere on the page.
    useEffect(() => {
        if (refreshTick === 0) {
            // Mount render: `exams` is still the empty placeholder, not a loaded list. Taking it as
            // the baseline would make the first real list look like a change and announce an exam
            // that was already open when the student arrived.
            return;
        }
        const previous = startableIdsRef.current;
        startableIdsRef.current = new Set(exams.filter(isStartable).map(examKey));
        if (previous === null) {
            return;
        }
        exams.filter((exam) => isStartable(exam) && !previous.has(examKey(exam)))
            .forEach((exam) => toast.info(
                t("pages.myExams.examStartedNotice", {name: exam.examName}),
                {toastId: `exam-started-${examKey(exam)}`}
            ));
    }, [exams, refreshTick, t]);

    const renderStatusBadge = (status) => {
        if (!status) return "-";
        const normalized = String(status).toUpperCase();
        const className =
            normalized === "ACTIVE" || normalized === "PASS"
                ? styles.statusActive
                : normalized === "CANCELLED" || normalized === "FAIL"
                    ? styles.statusCancelled
                    : styles.statusDefault;

        return <span className={className}>{toStatusAz(normalized)}</span>;
    };


    const renderMyStatusBadge = (status) => {
        if (!status) return "-";
        const normalized = String(status).toUpperCase();

        const className =
            normalized === "CONFIRMED"
                ? styles.myStatusConfirmed
                : normalized === "CANCELLED"
                    ? styles.myStatusCancelled
                    : normalized === "DISQUALIFIED"
                        ? styles.myStatusDisqualified
                        : normalized === "SUBMITTED"
                            ? styles.myStatusSubmitted
                            : normalized === "AUTO_SUBMITTED"
                                ? styles.myStatusAutoSubmitted
                                : normalized === "IN_PROGRESS"
                                    ? styles.myStatusInProgress
                                    : styles.myStatusDefault;

        return <span className={className}>{toStatusAz(normalized)}</span>;
    };



    return (
        <div className={styles.container}>
            <PageHeader
                title={t("pages.myExams.title")}
                subtitle=""
            />

            {errorKey && <div className={styles.error}>{t(errorKey)}</div>}

            {loading ? (
                <Loader fullscreen/>
            ) : exams.length === 0 ? (
                <div className={styles.cardsWrapper}>
                    <TableEmptyState
                        title={t("pages.myExams.noExamsTitle")}
                        description={t("pages.myExams.noExamsDescription")}
                    />
                </div>
            ) : (
                <div className={styles.cardsWrapper}>
                    {exams.map((exam) => {
                        const status = (exam.status || "").toUpperCase();
                        const myStatus = (exam.myStatus || "").toUpperCase();
                        const canStartExam = isStartable(exam);
                        const showWaitingText = status === "ACTIVE";
                        const showMyStatus = Boolean(myStatus);
                        const idForSession = examKey(exam);
                        const score = exam.score ?? 0;
                        const correctCount = exam.correctCount;
                        const totalQuestions = exam.totalQuestions;
                        const studentExamResultStatus = exam.studentExamResultStatus ?? "";
                        const attemptId = exam.attemptId ?? "";
                        const attemptStatus = exam.attemptStatus ?? "";
                        const cancelResultReason = exam.cancelResultReason ?? "";
                        const mode = myStatus === "IN_PROGRESS" ? "resume" : "start";

                        return (
                            <article key={exam.id} className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <div className={styles.cardTitleRow}>
                                        <h3 className={styles.cardTitle}>{exam.examName}</h3>
                                        {renderStatusBadge(exam.status)}
                                    </div>
                                    <div className={styles.cardMeta}>
                                            <span className={styles.cardMetaItem}>
                                              <FiClock/>
                                                {formatDateTime(exam.scheduledStartTime)}
                                            </span>
                                        {exam.proctored && (
                                            <span className={styles.cardMetaItem}>
                                                <FiVideo/>
                                                {t("pages.myExams.proctored")}
                                              </span>
                                        )}
                                        {(myStatus === 'SUBMITTED' || myStatus === 'CONFIRMED' || myStatus === 'AUTO_SUBMITTED') &&
                                            <span className={styles.cardMetaItem}>
                                                {t("pages.myExams.scoreLabel", {score})}
                                                {totalQuestions != null && (
                                                    <>{" · "}{t("pages.myExams.correctCount", {correct: correctCount ?? 0, total: totalQuestions})}</>
                                                )}
                                                {" "}{renderStatusBadge(studentExamResultStatus)}
                                            </span>
                                        }
                                    </div>
                                </div>
                                {exam.note && (
                                    <p className={styles.cardNote}>{exam.note}</p>
                                )}
                                <div className={styles.cardFooter}>
                                    <div className={styles.cardStatus}>
                                        {showMyStatus ? (
                                            <>
                                                {renderMyStatusBadge(exam.myStatus)}
                                                <button
                                                    type="button"
                                                    className={styles.iconButton}
                                                    disabled={resultLoading}
                                                    onClick={() => {
                                                        navigate(`/exam-attempts/${attemptId}/logs`, {
                                                            state: {
                                                                score: score,
                                                                attemptStatus: attemptStatus,
                                                                cancelResultReason: cancelResultReason,
                                                                finalStatus: studentExamResultStatus
                                                            }
                                                        });
                                                    }}
                                                    title={t("pages.myExams.viewResult")}
                                                >
                                                    <FiEye/>
                                                    <span>{t("pages.myExams.resultBtn")}</span>
                                                </button>
                                            </>
                                        ) : canStartExam ? null : showWaitingText ? (
                                            <span className={styles.waitingText}>{t("pages.myExams.waitingText")}</span>
                                        ) : null}
                                    </div>
                                    {canStartExam ? (
                                        <button
                                            type="button"
                                            className={styles.primaryButton}
                                            onClick={() =>
                                                navigate(`/exam-session/${idForSession}`, {
                                                    state: {mode, proctored: exam.proctored === true},
                                                })
                                            }
                                        >
                                            {mode === "resume" ? t("pages.myExams.resume") : t("pages.myExams.startExam")}
                                        </button>
                                    ) : null}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyExamsPage;
